import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { UserLabelPipe } from '../user-label-pipe';
import { UserService } from '../user-service';
import { realmRoles, UserRole } from '../user.model';

@Component({
  selector: 'app-select-user',
  imports: [CommonModule, MatFormFieldModule, MatSelectModule, UserLabelPipe],
  templateUrl: './select-user.html',
  styleUrls: ['./select-user.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectUser),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectUser),
      multi: true,
    },
  ],
})
export class SelectUser implements OnInit, ControlValueAccessor, Validator {
  readonly userService = inject(UserService);
  private injector = inject(Injector);

  @Input() label = 'Utilisateur';
  @Input() value: string | undefined | null;
  @Input() required: boolean | string | null | undefined;
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() userRoles: UserRole[] = realmRoles;
  ngControl?: NgControl;
  disabled = false;
  cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  onChange: (value: string | undefined | null) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: string | undefined | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | undefined | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.isRequired || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && (this.value === undefined || this.value === null || this.value === '')) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  /** Retourne le control du FormGroup / FormControl auquel ce composant est lié, si présent */
  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: string | undefined | null): void {
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
  }

  get isRequired(): boolean {
    if (this.required !== undefined && this.required !== null && this.required !== '') {
      if (typeof this.required === 'boolean') {
        return this.required;
      }
      return this.required === 'true';
    }

    return this.control?.hasValidator(Validators.required) ?? false;
  }
}
